import { useEffect, useState, useMemo } from 'react';
import { supabase } from '../../../lib/supabase';
import type { LedgerHeader, LedgerLineItem } from '../../../types';
import { Search, Eye, Trash2, CheckSquare, Square, Download, CheckCircle2, ChevronDown, AlertTriangle, X, Link2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { ReceiptModal } from './ReceiptModal';
import InvoicePreview from '../../mechanic/Invoice/InvoicePreview';
import { JobLinkModal } from './JobLinkModal';
import { jobLinkMethodLabel } from './jobLink';
import type { JobLinkReview } from '../../../types/finance';
import { useFinancialContext } from '../../../context/FinancialContext';
import { useConfirm } from '../../../components/ui/ConfirmProvider';

// Helper for formatting currency
const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
    }).format(amount);
};

// UK Date Formatter (dd-mm-yyyy)
const formatUKDate = (dateString: string | undefined | null) => {
    if (!dateString) return 'Unknown';
    try {
        const d = new Date(dateString);
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-');
    } catch {
        return dateString;
    }
};

interface JobLinkFields {
    id: string;
    vendor?: string | null;
    job_id?: string | null;
    job_link_method?: string | null;
    /** Set by fn_match_expense_job when the upload could not decide the job. */
    job_link_review?: JobLinkReview | null;
}

const jobLinkTargetFor = (expense: JobLinkFields) => ({
    id: expense.id,
    vendor: expense.vendor ?? null,
    jobId: expense.job_id ?? null,
    linkMethod: expense.job_link_method ?? null,
    review: expense.job_link_review ?? null,
});

/** Linked, waiting for the owner to pick the job, or neither. */
const jobLinkState = (expense: JobLinkFields): 'linked' | 'review' | 'none' =>
    expense.job_id ? 'linked' : expense.job_link_review ? 'review' : 'none';

const JOB_LINK_TONE = {
    linked: 'text-gold bg-gold-fill/10 border-gold/25 hover:bg-gold-fill/15',
    review: 'text-warning bg-warning/10 border-warning/30 hover:bg-warning/15',
    none: 'text-fg-3 hover:text-fg bg-card hover:bg-raised border-line',
} as const;

export function LiveLedgerTab() {
    // Non-VAT-registered orgs can't reclaim VAT, so the gross figure IS the true
    // cost. Showing a separate "net" alongside it is noise (and for these orgs the
    // two are usually identical anyway). Only surface the net column when the org
    // is VAT-registered and the split is meaningful.
    const { is_vat_registered, vat_registered_from } = useFinancialContext();

    // Per-line display cost, registration + date-aware — same rule as
    // fn_get_org_financials: a registered (and on/after its registration date)
    // purchase shows NET (input VAT is reclaimable); otherwise the line carries
    // its share of the header's VAT, so the breakdown sums to the gross header
    // total the non-registered trader actually paid.
    const lineCost = (item: { total: number | null }, expense: { date?: string | null; total_gross?: number | null; vat_amount?: number | null }): number => {
        const net = Number(item.total) || 0;
        const reclaimable = is_vat_registered
            && (!vat_registered_from || (expense.date ?? '') >= vat_registered_from);
        if (reclaimable) return net;
        const den = (Number(expense.total_gross) || 0) - (Number(expense.vat_amount) || 0);
        const vatShare = den <= 0 ? 0 : net * ((Number(expense.vat_amount) || 0) / den);
        return net + vatShare;
    };

    const [expenses, setExpenses] = useState<(LedgerHeader & { ledger_line_items: LedgerLineItem[], document_url?: string, uploaded_at?: string })[]>([]);

    const confirm = useConfirm();
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState<(LedgerHeader & { ledger_line_items: LedgerLineItem[], document_url?: string, uploaded_at?: string })[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

    // Dynamic state
    const [expenseCategories, setExpenseCategories] = useState<{ id: string, name: string }[]>([]);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [previewVendor, setPreviewVendor] = useState<string>('');
    const [viewInvoiceId, setViewInvoiceId] = useState<string | null>(null);
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
    const [jobLinkTarget, setJobLinkTarget] = useState<{ id: string; vendor: string | null; jobId: string | null; linkMethod: string | null; review: JobLinkReview | null } | null>(null);
    // Keep the ledger compact by default — show the 10 most recent, expand to the
    // full loaded set (up to 30). Search results (already scoped) show in full.
    const [showAll, setShowAll] = useState(false);

    useEffect(() => {
        fetchCategories();
        fetchLatestExpenses();
    }, []);

    const fetchCategories = async () => {
        try {
            const { data, error } = await supabase
                .from('expense_categories')
                .select('id, name')
                .order('name');
            if (error) throw error;
            if (data) {
                setExpenseCategories(data);
            }
        } catch (err) {
            console.error('Could not load expense categories:', err);
        }
    };

    // Build a lookup map: UUID -> name (safety net for any UUID-based category values)
    const categoryIdMap = useMemo(() => {
        const map: Record<string, string> = {};
        expenseCategories.forEach(c => { map[c.id] = c.name; });
        return map;
    }, [expenseCategories]);

    // Resolve a category value — if it's a UUID, look up the name; otherwise return as-is
    const resolveCategoryDisplay = (category: string | null | undefined): string => {
        if (!category) return 'Uncategorised';
        // Check if it looks like a UUID
        if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(category)) {
            return categoryIdMap[category] || 'Uncategorised';
        }
        return category;
    };

    const fetchLatestExpenses = async () => {
        try {
            setLoading(true);
            const { data, error } = await supabase
                .from('ledger_headers')
                .select('*, ledger_line_items(*), uploaded_invoices!fk_uploads_header_composite(file_path, created_at)')
                .eq('transaction_type', 'expense')
                .is('deleted_at', null)
                .order('date', { ascending: false })
                .limit(30);

            if (error) throw error;

            const mappedData = (data || []).map(expense => {
                const uploads = expense.uploaded_invoices as any[];
                const uploadPath = uploads && uploads.length > 0 ? uploads[0].file_path : null;
                const finalDocUrl = uploadPath || expense.document_url || null;
                return {
                    ...expense,
                    document_url: finalDocUrl,
                    uploaded_at: uploads && uploads.length > 0 ? uploads[0].created_at : expense.created_at
                };
            });

            setExpenses(mappedData);
        } catch (err) {
            console.error('Could not load expenses:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = async (term: string) => {
        setSearchTerm(term);
        if (!term) {
            setIsSearching(false);
            return;
        }

        setIsSearching(true);
        try {
            const { data, error } = await supabase
                .from('ledger_headers')
                .select('*, ledger_line_items(*), uploaded_invoices!fk_uploads_header_composite(file_path, created_at)')
                .eq('transaction_type', 'expense')
                .is('deleted_at', null)
                .or(`vendor.ilike.%${term}%, total_gross.eq.${parseFloat(term) || 0}`)
                .order('date', { ascending: false })
                .limit(30);

            if (!error) {
                const mappedData = (data || []).map(expense => {
                    const uploads = expense.uploaded_invoices as any[];
                    const uploadPath = uploads && uploads.length > 0 ? uploads[0].file_path : null;
                    return {
                        ...expense,
                        document_url: uploadPath || expense.document_url || null,
                        uploaded_at: uploads && uploads.length > 0 ? uploads[0].created_at : expense.created_at
                    };
                });
                setSearchResults(mappedData);
            }
        } catch (err) {
            console.error('Expense search failed:', err);
        }
    };

    // --- Bulk & Selection Logic ---
    const displayData = isSearching ? searchResults : expenses;
    // Collapse the default (non-search) view to the latest 10 unless expanded.
    const COLLAPSED_COUNT = 10;
    const isCollapsible = !isSearching && displayData.length > COLLAPSED_COUNT;
    const visibleData = isCollapsible && !showAll ? displayData.slice(0, COLLAPSED_COUNT) : displayData;

    const toggleSelectAll = () => {
        if (selectedIds.size === displayData.length && displayData.length > 0) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(displayData.map(d => d.id)));
        }
    };

    const toggleSelect = (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        const next = new Set(selectedIds);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setSelectedIds(next);
    };

    const toggleRowExpansion = (id: string, e?: React.MouseEvent) => {
        if (e) e.stopPropagation();
        const next = new Set(expandedRows);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setExpandedRows(next);
    };

    const handleBulkDelete = async () => {
        if (selectedIds.size === 0) return;
        if (!await confirm({ title: 'Move to the Bin?', message: `${selectedIds.size} expenses will move to the Bin. You can restore them later.`, confirmText: 'Move to bin' })) return;

        try {
            const { error } = await supabase
                .from('ledger_headers')
                .update({ deleted_at: new Date().toISOString() })
                .in('id', Array.from(selectedIds));

            if (error) throw error;

            setExpenses(prev => prev.filter(exp => !selectedIds.has(exp.id)));
            if (isSearching) {
                setSearchResults(prev => prev.filter(exp => !selectedIds.has(exp.id)));
            }
            setSelectedIds(new Set());
            toast.success(`${selectedIds.size} items moved to bin`);
        } catch {
            toast.error('Failed to move to bin');
        }
    };

    const handleBulkExport = () => {
        if (selectedIds.size === 0) return;

        try {
            const itemsToExport = displayData.filter(d => selectedIds.has(d.id));
            const csvContent = "data:text/csv;charset=utf-8,"
                + "Document Date,Upload Date,Vendor,Category,Total (Inc VAT),Source\n"
                + itemsToExport.map(e => {
                    const cat = Array.from(new Set(e.ledger_line_items?.map((l: any) => l.category) || []))[0] || 'Uncategorised';
                    return `${formatUKDate(e.date)},${formatUKDate(e.uploaded_at)},"${e.vendor || ''}",${cat},${e.total_gross},${e.created_by === 'system' ? 'OCR' : 'Manual'}`;
                }).join("\n");

            const encodedUri = encodeURI(csvContent);
            const link = document.createElement("a");
            link.setAttribute("href", encodedUri);
            link.setAttribute("download", `expenses_export_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            toast.success(`Exported ${selectedIds.size} items`);
            setSelectedIds(new Set());
        } catch {
            toast.error('Failed to generate export');
        }
    };

    const handleVerifyExpense = async (expense: any) => {
        try {
            const { error } = await supabase
                .from('ledger_headers')
                .update({ status: 'posted' })
                .eq('id', expense.id)
                .eq('org_id', expense.org_id);

            if (error) throw error;

            // Optimistic Update
            setExpenses(prev => prev.map(exp => exp.id === expense.id ? { ...exp, status: 'posted' } : exp));
            if (isSearching) {
                setSearchResults(prev => prev.map(exp => exp.id === expense.id ? { ...exp, status: 'posted' } : exp));
            }
            toast.success('Invoice Verified');
        } catch {
            toast.error('Failed to verify invoice');
        }
    };

    // --- Row Actions ---
    const handleCategoryChange = async (headerId: string, newCategory: string, lineItemId?: string) => {
        // Find the expense to get org_id for the partitioned table
        const expense = displayData.find(e => e.id === headerId);
        if (!expense) return;

        try {
            setExpenses(prev => prev.map(exp => {
                if (exp.id === headerId) {
                    if (lineItemId != null) {
                        return {
                            ...exp,
                            ledger_line_items: exp.ledger_line_items.map((l: any) => l.id === lineItemId ? { ...l, category: newCategory } : l)
                        };
                    }
                    return {
                        ...exp,
                        ledger_line_items: exp.ledger_line_items.map((l: any) => ({ ...l, category: newCategory }))
                    };
                }
                return exp;
            }));

            if (isSearching) setSearchResults(prev => prev.map(exp => {
                if (exp.id === headerId) {
                    if (lineItemId != null) {
                        return {
                            ...exp,
                            ledger_line_items: exp.ledger_line_items.map((l: any) => l.id === lineItemId ? { ...l, category: newCategory } : l)
                        };
                    }
                    return {
                        ...exp,
                        ledger_line_items: exp.ledger_line_items.map((l: any) => ({ ...l, category: newCategory }))
                    };
                }
                return exp;
            }));

            let dbQuery = supabase
                .from('ledger_line_items')
                .update({ category: newCategory })
                .eq('header_id', headerId)
                .eq('org_id', expense.org_id);

            if (lineItemId != null) {
                dbQuery = dbQuery.eq('id', lineItemId);
            }

            const { data, error } = await dbQuery.select();

            if (error) throw error;
            if (!data || data.length === 0) {

                // Fallback: reconstruct the missing line item for older corrupted records
                const netAmount = expense.total_net ?? (expense.total_gross - (expense.vat_amount || 0));

                const { error: insertError } = await supabase
                    .from('ledger_line_items')
                    .insert({
                        org_id: expense.org_id,
                        header_id: expense.id,
                        header_date: expense.date,
                        category: newCategory,
                        description: expense.description || expense.vendor || 'Restored Line Item',
                        quantity: 1,
                        unit_price: netAmount,
                        total: netAmount
                    });

                if (insertError) {
                    throw insertError;
                }
            }
            toast.success('Category updated');
        } catch {
            toast.error("Failed to update category");
            fetchLatestExpenses(); // Revert on fail
        }
    };

    const handleViewReceipt = async (expense: any) => {
        const vendor = expense.vendor || 'Unknown Vendor';
        const url = expense.document_url;

        // 1. Uploaded supplier document → preview inline (no navigating away).
        if (url) {
            try {
                const loadingToast = toast.loading('Locating receipt...');
                if (url.startsWith('http')) {
                    toast.dismiss(loadingToast);
                    setPreviewUrl(url);
                    setPreviewVendor(vendor);
                    return;
                }
                const { data, error } = await supabase.storage
                    .from('expense-receipts')
                    .createSignedUrl(url, 300);
                toast.dismiss(loadingToast);
                if (error) throw error;
                if (data?.signedUrl) {
                    setPreviewUrl(data.signedUrl);
                    setPreviewVendor(vendor);
                } else {
                    toast.error('Could not generate receipt link.');
                }
            } catch {
                toast.error('Failed to open receipt.');
            }
            return;
        }

        // 2. System-generated payment fee → no document; show the breakdown.
        if (vendor.startsWith('Payment Fees')) {
            const lineItems = expense.ledger_line_items || [];
            const feeDetails = lineItems.map((li: any) => `${li.description}: £${Number(li.total || 0).toFixed(2)}`).join('\n');
            toast(
                `This is a system-generated fee deduction.\n\n${feeDetails || `Total: £${Number(expense.total_gross || 0).toFixed(2)}`}`,
                { icon: 'ℹ️', duration: 5000, style: { whiteSpace: 'pre-line' } }
            );
            return;
        }

        // 3. Record derived from an invoice (e.g. auto-COGS) → open that invoice
        //    in the same preview modal as the Invoices page (print/download there).
        if (expense.job_id) {
            const loadingToast = toast.loading('Opening invoice...');
            const { data: inv, error } = await supabase
                .from('invoices')
                .select('id')
                .eq('org_id', expense.org_id)
                .eq('job_id', expense.job_id)
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle();
            toast.dismiss(loadingToast);
            if (!error && inv?.id) {
                setViewInvoiceId(inv.id);
                return;
            }
            toast.error('No invoice found for this record.');
            return;
        }

        toast.error('No receipt attached to this expense.');
    };
    const handleMoveToBin = async (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!await confirm({ title: 'Move to the Bin?', message: 'You can restore it from the Bin later.', confirmText: 'Move to bin' })) return;

        try {
            const { error } = await supabase
                .from('ledger_headers')
                .update({ deleted_at: new Date().toISOString() })
                .eq('id', id);

            if (error) throw error;

            setExpenses(prev => prev.filter(exp => exp.id !== id));
            if (isSearching) {
                setSearchResults(prev => prev.filter(exp => exp.id !== id));
            }
            // Remove from selection if deleted
            setSelectedIds(prev => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
            toast.success('Moved to Bin');
        } catch {
            toast.error('Failed to move to bin');
        }
    };

    return (
        <div className="space-y-6">
            <ReceiptModal
                url={previewUrl}
                vendor={previewVendor}
                onClose={() => {
                    setPreviewUrl(null);
                    setPreviewVendor('');
                }}
            />

            <JobLinkModal
                isOpen={jobLinkTarget !== null}
                onClose={() => setJobLinkTarget(null)}
                onSuccess={() => fetchLatestExpenses()}
                headerId={jobLinkTarget?.id ?? null}
                headerVendor={jobLinkTarget?.vendor ?? null}
                currentJobId={jobLinkTarget?.jobId ?? null}
                linkMethod={jobLinkTarget?.linkMethod ?? null}
                review={jobLinkTarget?.review ?? null}
            />

            {viewInvoiceId && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-scrim/80 backdrop-blur-sm p-4 overflow-y-auto">
                    <div className="bg-panel w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-xl border border-tint/10 shadow-2xl animate-in zoom-in-95">
                        <div className="sticky top-0 z-10 px-6 py-4 bg-panel border-b border-tint/10 flex items-center justify-between">
                            <h2 className="text-lg font-bold text-fg">Invoice</h2>
                            <button
                                onClick={() => setViewInvoiceId(null)}
                                className="p-2 text-fg-3 hover:text-fg hover:bg-tint/10 rounded-lg"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-6">
                            <InvoicePreview invoiceId={viewInvoiceId} />
                        </div>
                    </div>
                </div>
            )}
            {/* Header Actions & Search */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div className="relative flex-1 w-full max-w-md">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-fg-4 w-4 h-4 pointer-events-none" />
                    <input
                        type="text"
                        placeholder="Search Archive (Vendor or Amount)..."
                        value={searchTerm}
                        onChange={(e) => handleSearch(e.target.value)}
                        className="w-full h-12 bg-card border border-line rounded-xl pl-11 pr-4 text-base text-fg placeholder:text-fg-4 focus:outline-none focus:border-gold focus:shadow-focus transition-all"
                    />
                </div>

                {selectedIds.size > 0 && (
                    <div className="flex items-center gap-2 w-full md:w-auto animate-in fade-in slide-in-from-bottom-2 duration-300">
                        <span className="text-sm font-medium text-fg-3 mr-2">{selectedIds.size} selected</span>
                        <button
                            onClick={handleBulkExport}
                            className="flex-1 md:flex-none flex items-center justify-center gap-2 h-10 px-4 bg-card hover:bg-raised text-fg rounded-lg text-sm font-medium border border-line transition-colors shadow-sm"
                        >
                            <Download className="w-4 h-4" />
                            <span className="hidden sm:inline">Export</span>
                        </button>
                        <button
                            onClick={handleBulkDelete}
                            className="flex-1 md:flex-none flex items-center justify-center gap-2 h-10 px-4 bg-danger/10 hover:bg-danger/20 text-danger rounded-lg text-sm font-medium border border-danger/20 hover:border-danger/30 transition-colors shadow-sm"
                        >
                            <Trash2 className="w-4 h-4" />
                            <span className="hidden sm:inline">Delete</span>
                        </button>
                    </div>
                )}
            </div>

            {/* Main Content Area */}
            <div className="bg-transparent md:border border-line md:rounded-xl md:overflow-hidden backdrop-blur-sm">

                {/* Mobile View - Receipt Slips */}
                <div className="md:hidden flex flex-col gap-5 pt-2">
                    {loading ? (
                        <div className="p-8 text-center text-fg-2 text-sm animate-pulse">Loading ledgers...</div>
                    ) : displayData.length === 0 ? (
                        <div className="p-8 text-center text-fg-2 text-sm">No expenses found for this criteria.</div>
                    ) : (
                        visibleData.map((expense) => {
                            const isSelected = selectedIds.has(expense.id);

                            return (
                                <div
                                    key={expense.id}
                                    className={`relative bg-card mx-4 border rounded-xl overflow-hidden transition-all ${isSelected ? 'border-gold ring-1 ring-gold shadow-[0_0_12px_var(--fd-gold-glow)]' : 'border-line shadow-xl'
                                        }`}
                                >
                                    {/* Selection Target overlay */}
                                    <div
                                        className="absolute top-4 right-4 z-10 p-2 -m-2 cursor-pointer"
                                        onClick={(e) => toggleSelect(expense.id, e)}
                                    >
                                        {isSelected ? <CheckSquare className="w-5 h-5 text-gold" /> : <Square className="w-5 h-5 text-fg-3" />}
                                    </div>

                                    <div className="p-5 flex flex-col gap-4">
                                        {expense.status === 'needs_review' && (
                                            <div className="flex items-center gap-2 bg-gold-fill/10 border border-gold/20 text-gold px-3 py-2 rounded-md mb-2">
                                                <AlertTriangle className="w-4 h-4" />
                                                <span className="text-xs font-semibold uppercase tracking-wider">Review Required</span>
                                                <button
                                                    onClick={() => handleVerifyExpense(expense)}
                                                    className="ml-auto text-xs px-2 py-1 bg-gold-fill/10 border border-gold/20 text-gold font-bold rounded hover:bg-gold-fill/10 transition-colors"
                                                >
                                                    Verify
                                                </button>
                                            </div>
                                        )}
                                        <div className="flex justify-between items-start pr-8">
                                            <div className="flex items-center gap-2">
                                                <h3 className="font-bold text-fg text-[15px] leading-tight break-words max-w-[180px]">{expense.vendor || 'Unknown Vendor'}</h3>
                                                {expense.created_by === 'system' && expense.status !== 'needs_review' && (
                                                    <span className="flex-shrink-0 flex items-center justify-center w-5 h-5 rounded-full bg-success/10 text-success" title="OCR Verified">
                                                        <CheckCircle2 className="w-3.5 h-3.5" />
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-y-3 gap-x-4 border-y border-dashed border-line py-4 my-1">
                                            <div>
                                                <span className="text-fd-3xs uppercase tracking-wider text-fg-4 block mb-1">Doc Date</span>
                                                <span className="text-sm font-medium text-fg">{formatUKDate(expense.date)}</span>
                                            </div>
                                            <div>
                                                <span className="text-fd-3xs uppercase tracking-wider text-fg-4 block mb-1">Uploaded</span>
                                                <span className="text-sm text-fg-3">{formatUKDate(expense.uploaded_at)}</span>
                                            </div>
                                            {is_vat_registered && (
                                                <div className="col-span-1">
                                                    <span className="text-fd-3xs uppercase tracking-wider text-fg-4 block mb-2">Net Amount</span>
                                                    <div className="font-bold text-[18px] text-fg-3 font-mono tracking-tight">
                                                        {formatCurrency(expense.total_net ?? (expense.total_gross - (expense.vat_amount || 0)))}
                                                    </div>
                                                </div>
                                            )}
                                            <div className={is_vat_registered ? 'col-span-1' : 'col-span-2'}>
                                                <span className="text-fd-3xs uppercase tracking-wider text-fg-4 block mb-2">{is_vat_registered ? 'Gross Amount' : 'Amount'}</span>
                                                <div className="font-bold text-[22px] text-fg font-mono tracking-tight">
                                                    {formatCurrency(expense.total_gross)}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex flex-col gap-3">
                                            <span className="text-fd-3xs uppercase tracking-wider text-fg-4 block">Line Items</span>
                                            {expense.ledger_line_items?.slice(0, expandedRows.has(expense.id) ? undefined : 3).map((item: any) => (
                                                <div key={item.id} className="flex flex-col gap-1.5 p-3 bg-raised rounded-lg border border-line">
                                                    <div className="flex justify-between items-start">
                                                        <span className="text-sm text-fg font-medium line-clamp-2">{item.description}</span>
                                                        <span className="text-sm font-mono text-fg whitespace-nowrap ml-3">{formatCurrency(lineCost(item, expense))}</span>
                                                    </div>
                                                    <div className="relative mt-1">
                                                        <select
                                                            value={resolveCategoryDisplay(item.category)}
                                                            onChange={(e) => handleCategoryChange(expense.id, e.target.value, item.id)}
                                                            className="w-full appearance-none bg-card text-success font-medium text-xs py-2 pl-3 pr-8 rounded border border-line focus:outline-none focus:border-gold transition-colors"
                                                        >
                                                            <option value="" disabled hidden>Select Category</option>
                                                            {expenseCategories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                                                            {!expenseCategories.some(c => c.name === resolveCategoryDisplay(item.category)) && item.category && (
                                                                <option value={resolveCategoryDisplay(item.category)}>{resolveCategoryDisplay(item.category)}</option>
                                                            )}
                                                        </select>
                                                        <ChevronDown className="w-3.5 h-3.5 text-success/50 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                                                    </div>
                                                </div>
                                            ))}

                                            {expense.ledger_line_items?.length > 3 && (
                                                <button
                                                    onClick={(e) => toggleRowExpansion(expense.id, e)}
                                                    className="text-xs font-semibold text-fg-3 hover:text-fg flex items-center justify-center gap-1 py-1.5 transition-colors border border-dashed border-line rounded-md hover:bg-raised"
                                                >
                                                    {expandedRows.has(expense.id) ? 'Show Less' : `+ ${expense.ledger_line_items.length - 3} More Items`}
                                                </button>
                                            )}
                                        </div>

                                        <div className="flex gap-2 pt-2">
                                            <button
                                                onClick={() => handleViewReceipt(expense)}
                                                className="flex-1 flex items-center justify-center gap-2 min-h-[44px] rounded-lg font-semibold text-sm transition-all bg-card hover:bg-raised text-fg border border-line active:scale-[0.98] shadow-sm"
                                            >
                                                <Eye className="w-4 h-4" />
                                                View Receipt
                                            </button>
                                            <button
                                                onClick={() => setJobLinkTarget(jobLinkTargetFor(expense))}
                                                aria-label={jobLinkState(expense) === 'review' ? 'Check which job this is for' : expense.job_id ? 'Edit job link' : 'Link to a job'}
                                                className={`flex items-center justify-center min-h-[44px] w-[50px] rounded-lg font-semibold border transition-all active:scale-[0.98] shadow-sm ${JOB_LINK_TONE[jobLinkState(expense)]}`}
                                            >
                                                {jobLinkState(expense) === 'review'
                                                    ? <AlertTriangle className="w-4 h-4" />
                                                    : <Link2 className="w-4 h-4" />}
                                            </button>
                                            <button
                                                onClick={(e) => handleMoveToBin(expense.id, e)}
                                                className="flex items-center justify-center min-h-[44px] w-[50px] rounded-lg font-semibold bg-card border border-line text-fg-3 hover:text-danger hover:bg-danger/10 hover:border-danger/20 transition-all active:scale-[0.98] shadow-sm"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Desktop View - Advanced Table */}
                <div className="hidden md:block overflow-x-auto w-full">
                    <table className="w-full text-left text-sm whitespace-nowrap">
                        <thead className="bg-card border-b border-line text-fg-3 text-xs font-display font-semibold uppercase tracking-wider">
                            <tr>
                                <th className="px-5 py-4 w-12 text-center">
                                    <button onClick={toggleSelectAll} className="p-1 -m-1 text-fg-3 hover:text-fg transition-colors">
                                        {selectedIds.size === displayData.length && displayData.length > 0 ? <CheckSquare className="w-5 h-5 text-gold" /> : <Square className="w-5 h-5" />}
                                    </button>
                                </th>
                                <th className="px-6 py-4">Date Information</th>
                                <th className="px-6 py-4">Vendor</th>
                                <th className="px-6 py-4">Line Items</th>
                                {is_vat_registered && <th className="px-6 py-4 text-right">Net Total</th>}
                                <th className="px-6 py-4 text-right">{is_vat_registered ? 'Gross Total' : 'Total'}</th>
                                <th className="px-8 py-4 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-line bg-card">
                            {loading ? (
                                <tr><td colSpan={is_vat_registered ? 7 : 6} className="px-6 py-12 text-center text-fg-2 animate-pulse">Loading ledgers...</td></tr>
                            ) : displayData.length === 0 ? (
                                <tr><td colSpan={is_vat_registered ? 7 : 6} className="px-6 py-12 text-center text-fg-2">No expenses found for this criteria.</td></tr>
                            ) : (
                                visibleData.map((expense) => {
                                    const isSelected = selectedIds.has(expense.id);

                                    return (
                                        <tr key={expense.id} className={`group transition-colors ${isSelected ? 'bg-raised' : 'hover:bg-raised'} ${expense.status === 'needs_review' ? 'bg-gold-fill/5' : ''}`}>
                                            <td className="px-5 py-4 text-center align-top">
                                                <button onClick={(e) => toggleSelect(expense.id, e)} className="p-1 -m-1 mt-1 text-fg-3 hover:text-fg transition-colors">
                                                    {isSelected ? <CheckSquare className="w-5 h-5 text-gold" /> : <Square className="w-5 h-5" />}
                                                </button>
                                            </td>
                                            <td className="px-6 py-4 align-top">
                                                <div className="flex flex-col mt-1">
                                                    <span className="text-fg font-medium">Date: {formatUKDate(expense.date)}</span>
                                                    <span className="text-fg-4 text-fd-sm-tight mt-0.5 tracking-wide">Uploaded: {formatUKDate(expense.uploaded_at)}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 align-top">
                                                <div className="flex flex-col gap-1 mt-1">
                                                    <div className="flex items-center gap-2 text-fg font-medium break-words max-w-[200px]">
                                                        {expense.vendor || 'Unknown Vendor'}
                                                        {expense.created_by === 'system' && expense.status !== 'needs_review' && (
                                                            <span title="OCR Verified" className="flex items-center justify-center w-5 h-5 rounded-full bg-success/10 text-success border border-success/20 flex-shrink-0">
                                                                <CheckCircle2 className="w-3 h-3" />
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 min-w-[300px]">
                                                <div className="flex flex-col gap-2 w-full">
                                                    {expense.status === 'needs_review' && (
                                                        <div className="flex items-center gap-2 bg-gold-fill/10 border border-gold/20 text-gold px-3 py-1.5 rounded-md mb-2 w-fit">
                                                            <AlertTriangle className="w-3.5 h-3.5" />
                                                            <span className="text-fd-sm-tight font-semibold uppercase tracking-wider">Review Required</span>
                                                            <button
                                                                onClick={() => handleVerifyExpense(expense)}
                                                                className="ml-3 text-fd-3xs px-2 py-1 bg-gold-fill/10 border border-gold/20 text-gold font-bold rounded hover:bg-gold-fill/10 transition-colors"
                                                            >
                                                                VERIFY
                                                            </button>
                                                        </div>
                                                    )}
                                                    {expense.ledger_line_items?.slice(0, expandedRows.has(expense.id) ? undefined : 3).map((item: any) => (
                                                        <div key={item.id} className="flex items-center justify-between bg-card p-2 rounded border border-line">
                                                            <div className="flex flex-col gap-1.5 w-full mr-4">
                                                                <span className="text-xs text-fg font-medium line-clamp-1" title={item.description}>{item.description}</span>
                                                                <div className="relative w-48">
                                                                    <select
                                                                        value={resolveCategoryDisplay(item.category)}
                                                                        onChange={(e) => handleCategoryChange(expense.id, e.target.value, item.id)}
                                                                        className="w-full appearance-none bg-transparent border border-line text-success text-fd-sm-tight py-1 pl-2 pr-6 rounded focus:outline-none focus:border-gold cursor-pointer transition-colors"
                                                                    >
                                                                        <option value="" disabled hidden>Select Category</option>
                                                                        {expenseCategories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                                                                        {!expenseCategories.some(c => c.name === resolveCategoryDisplay(item.category)) && item.category && (
                                                                            <option value={resolveCategoryDisplay(item.category)}>{resolveCategoryDisplay(item.category)}</option>
                                                                        )}
                                                                    </select>
                                                            <ChevronDown className="w-3 h-3 text-success/50 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                                                                </div>
                                                            </div>
                                                            {(expense.ledger_line_items?.length ?? 0) > 1 && (
                                                                <span className="text-xs font-mono text-fg-3 whitespace-nowrap">{formatCurrency(lineCost(item, expense))}</span>
                                                            )}
                                                        </div>
                                                    ))}
                                                    {expense.ledger_line_items?.length > 3 && (
                                                        <button
                                                            onClick={(e) => toggleRowExpansion(expense.id, e)}
                                                            className="text-fd-sm-tight font-medium text-fg-3 hover:text-fg mt-1 w-fit transition-colors"
                                                        >
                                                            {expandedRows.has(expense.id) ? 'Show Less' : `+ ${expense.ledger_line_items.length - 3} More Items`}
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                            {is_vat_registered && (
                                                <td className="px-6 py-4 text-right font-mono text-[15px] font-medium text-fg-3 tracking-tight align-top pt-5">
                                                    {formatCurrency(expense.total_net ?? (expense.total_gross - (expense.vat_amount || 0)))}
                                                </td>
                                            )}
                                            <td className="px-6 py-4 text-right font-mono text-[16px] font-semibold text-fg tracking-tight align-top pt-5">
                                                {formatCurrency(expense.total_gross)}
                                            </td>
                                            <td className="px-8 py-4 text-right align-top pt-4">
                                                <div className="flex items-center justify-end gap-2 opacity-60 group-hover:opacity-100 transition-opacity">
                                                    <button
                                                        onClick={() => handleViewReceipt(expense)}
                                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors text-fg hover:bg-raised bg-card border border-line shadow-sm"
                                                    >
                                                        <Eye className="w-3.5 h-3.5" />
                                                        View
                                                    </button>
                                                    <button
                                                        onClick={() => setJobLinkTarget(jobLinkTargetFor(expense))}
                                                        title={jobLinkMethodLabel(expense.job_link_method) ?? (jobLinkState(expense) === 'review' ? 'The upload could not tell which job this is for' : 'Link to a job')}
                                                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors border shadow-sm ${JOB_LINK_TONE[jobLinkState(expense)]}`}
                                                    >
                                                        {jobLinkState(expense) === 'review'
                                                            ? <AlertTriangle className="w-3.5 h-3.5" />
                                                            : <Link2 className="w-3.5 h-3.5" />}
                                                        {{ linked: 'Linked', review: 'Check job', none: 'Link' }[jobLinkState(expense)]}
                                                    </button>
                                                    <button
                                                        onClick={(e) => handleMoveToBin(expense.id, e)}
                                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-fg-3 hover:text-danger bg-card hover:bg-danger/10 border border-transparent hover:border-danger/20 transition-colors"
                                                    >
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                        Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Collapse / expand — keep the ledger to the latest 10 unless
                    the user asks for the full loaded set (up to 30). */}
                {isCollapsible && (
                    <div className="flex justify-center py-4 border-t border-line">
                        <button
                            onClick={() => setShowAll((v) => !v)}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-fg-3 hover:text-gold bg-card hover:bg-raised border border-line transition-colors"
                        >
                            {showAll ? 'Show less' : `Show all (${displayData.length})`}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
